import { useState } from "react";
import useSWR from "swr";

import { Avatar } from "../components/Avatar";
import { Icon } from "../components/Icon";
import { useModalDismiss } from "../hooks/useModalDismiss";
import {
  api,
  type HistoryApprovalRow,
  type PendingApprovalRow,
} from "../services/api";

type Tab = "pending" | "history";

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-AU", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "Australia/Perth",
  });
}

function decisionPill(decision: string): string {
  if (decision === "approved") return "pill pill-approved";
  if (decision === "rejected") return "pill pill-rejected";
  return "pill pill-submitted";
}

/** Requestor cell with an initials avatar (consistent with My Requests). */
function Requestor({ name }: { name: string | null | undefined }) {
  const display = name ?? "—";
  return (
    <span className="t-holder">
      {name && <Avatar name={name} size="sm" />}
      <span>{display}</span>
    </span>
  );
}

/**
 * Approvals page — shows the approver their pending queue and decision history.
 *
 * Tabs:
 * - Pending: every assignment currently waiting on the caller. Approve / reject
 *   buttons open a confirmation modal that captures an optional comment.
 * - History: every assignment they have ever decided on, paginated.
 *
 * Tenant scoping is enforced server-side; this page renders whatever the API
 * returns and trusts the company filter on the request side.
 */
export function Approvals() {
  const [tab, setTab] = useState<Tab>("pending");
  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Approvals</h1>
        <p className="page-subtitle">
          Requests awaiting your decision, plus the calls you have already made.
        </p>
      </div>

      <div className="scope-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === "pending"}
          className={`scope-tab ${tab === "pending" ? "active" : ""}`}
          onClick={() => setTab("pending")}
        >
          Pending
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === "history"}
          className={`scope-tab ${tab === "history" ? "active" : ""}`}
          onClick={() => setTab("history")}
        >
          History
        </button>
      </div>

      {tab === "pending" ? <PendingPanel /> : <HistoryPanel />}
    </>
  );
}

function PendingPanel() {
  const { data, isLoading, error, mutate } = useSWR<PendingApprovalRow[]>(
    "/api/approvals/pending",
    () => api.approvals.listPending(),
  );

  const [decisionRow, setDecisionRow] = useState<{
    row: PendingApprovalRow;
    decision: "approve" | "reject";
  } | null>(null);

  return (
    <div className="card">
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Form</th>
              <th>Requestor</th>
              <th>Step</th>
              <th>Submitted</th>
              <th style={{ textAlign: "right" }}>Decide</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="ticket-empty" colSpan={5}>
                  Loading pending approvals...
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td className="ticket-empty" colSpan={5}>
                  {String((error as Error).message ?? error)}
                </td>
              </tr>
            )}
            {!isLoading && !error && (data ?? []).length === 0 && (
              <tr>
                <td className="ticket-empty" colSpan={5}>
                  Nothing waiting on you right now.
                </td>
              </tr>
            )}
            {!isLoading &&
              !error &&
              (data ?? []).map((row) => (
                <tr key={row.assignment_id}>
                  <td>
                    <strong>{row.form_type_slug ?? "(form)"}</strong>
                    <div className="form-field-meta" style={{ marginTop: 2 }}>
                      <span className="form-counter">
                        Request {row.request_id.slice(0, 8)}
                      </span>
                    </div>
                  </td>
                  <td>
                    <Requestor name={row.requestor_display_name} />
                  </td>
                  <td>
                    {row.step_name}{" "}
                    <span className="form-counter">({row.step_type})</span>
                  </td>
                  <td>{fmtDateTime(row.submitted_at)}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        setDecisionRow({ row, decision: "approve" })
                      }
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() =>
                        setDecisionRow({ row, decision: "reject" })
                      }
                    >
                      Reject
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {decisionRow && (
        <DecisionModal
          row={decisionRow.row}
          decision={decisionRow.decision}
          onClose={() => setDecisionRow(null)}
          onDone={async () => {
            setDecisionRow(null);
            await mutate();
          }}
        />
      )}
    </div>
  );
}

function HistoryPanel() {
  const { data, isLoading, error } = useSWR<HistoryApprovalRow[]>(
    "/api/approvals/history",
    () => api.approvals.listHistory(1, 50),
  );

  return (
    <div className="card">
      <div className="table-wrapper">
        <table>
          <thead>
            <tr>
              <th>Form</th>
              <th>Requestor</th>
              <th>Step</th>
              <th>Decision</th>
              <th>Decided</th>
              <th>Comment</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td className="ticket-empty" colSpan={6}>
                  Loading history...
                </td>
              </tr>
            )}
            {error && !isLoading && (
              <tr>
                <td className="ticket-empty" colSpan={6}>
                  {String((error as Error).message ?? error)}
                </td>
              </tr>
            )}
            {!isLoading && !error && (data ?? []).length === 0 && (
              <tr>
                <td className="ticket-empty" colSpan={6}>
                  You haven&apos;t decided on any requests yet.
                </td>
              </tr>
            )}
            {!isLoading &&
              !error &&
              (data ?? []).map((row) => (
                <tr key={row.assignment_id}>
                  <td>
                    <strong>{row.form_type_slug ?? "(form)"}</strong>
                  </td>
                  <td>
                    <Requestor name={row.requestor_display_name} />
                  </td>
                  <td>{row.step_name}</td>
                  <td>
                    <span className={decisionPill(row.decision)}>
                      <span className="dot" />
                      {row.decision}
                    </span>
                  </td>
                  <td>{fmtDateTime(row.decided_at)}</td>
                  <td>{row.decision_comment ?? ""}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface DecisionModalProps {
  row: PendingApprovalRow;
  decision: "approve" | "reject";
  onClose: () => void;
  onDone: () => void | Promise<void>;
}

const PARTIAL_APPROVAL_COPY: Record<string, string> = {
  ticket_failed:
    "Approved, but the Autotask ticket could not be created. Staff have been alerted.",
  ticket_failed_unalerted:
    "Approved, but the Autotask ticket could not be created. Please contact iQuest.",
  notification_failed: "Approved, but the approval email could not be sent.",
  fulfilment_incomplete:
    "Approved, but a follow-up step did not finish. Please contact iQuest.",
};

function partialApprovalMessage(warning: string): string {
  return (
    PARTIAL_APPROVAL_COPY[warning] ??
    "Approved, but a follow-up step did not finish. Please contact iQuest."
  );
}

function DecisionModal({ row, decision, onClose, onDone }: DecisionModalProps) {
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notices, setNotices] = useState<string[]>([]);
  const label = decision === "approve" ? "Approve" : "Reject";
  // The decision is already committed once notices are showing. Closing
  // has to refresh the queue, or the row stays under Pending.
  function dismiss() {
    if (notices.length > 0) {
      void onDone();
      return;
    }
    onClose();
  }
  const panelRef = useModalDismiss(dismiss);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (decision === "approve") {
        const result = await api.approvals.approve(row.request_id, comment || null);
        if (result.warnings && result.warnings.length > 0) {
          setNotices(result.warnings);
          return;
        }
      } else {
        await api.approvals.reject(row.request_id, comment || null);
      }
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to record decision");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => e.target === e.currentTarget && dismiss()}
    >
      <div className="modal-panel modal-panel-md" ref={panelRef}>
        <header className="modal-header">
          <div>
            <h2 className="modal-title">
              {label} {row.form_type_slug ?? "request"}
            </h2>
            <p className="modal-subtitle">
              {row.step_name} · submitted{" "}
              {fmtDateTime(row.submitted_at)} by{" "}
              {row.requestor_display_name ?? "—"}
            </p>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={dismiss}
            aria-label="Close"
          >
            ×
          </button>
        </header>
        <div className="modal-body">
          {notices.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {notices.map((warning) => (
                <div key={warning} className="iq-callout amber" role="status">
                  <Icon name="info" size={18} />
                  <span>{partialApprovalMessage(warning)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="form-field">
              <label htmlFor="decision-comment">Comment (optional)</label>
              <textarea
                id="decision-comment"
                maxLength={4000}
                rows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={
                  decision === "reject"
                    ? "Tell the requestor why so they can fix it."
                    : "Leave a note for the audit trail."
                }
              />
            </div>
          )}
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer className="modal-footer">
          {notices.length > 0 ? (
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => void onDone()}
            >
              Done
            </button>
          ) : (
            <>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => void submit()}
                disabled={busy}
              >
                {busy ? "Saving..." : label}
              </button>
            </>
          )}
        </footer>
      </div>
    </div>
  );
}
